import { useMemo } from 'react';

import { useAuthenticatedAxios } from './axiosInstance';

/**
 * ecosystemApi: client service for the platform-level ecosystem and
 * membership endpoints.
 *
 * The join / leave / list endpoints are PLATFORM-level (not tenant-scoped):
 * the server identifies the ecosystem by the `:slug` route param, not by the
 * resolved tenant. They are still called through the tenant-aware axios
 * instance: in path mode the interceptor prepends the active basePath, which
 * the server's tenant resolver harmlessly strips before reaching these
 * `/api/ecosystems/...` routes (the `ecosystems` slug is reserved, so these
 * requests always stay in platform mode server-side).
 *
 * P7 wave 3a adds the platform-admin CRUD methods (`fetchAllEcosystems`,
 * `createEcosystem`, `updateEcosystem`, `archiveEcosystem`). The server gates
 * these on `isPlatformAdmin`; a non-admin request gets a 403.
 *
 * P7 wave 3b adds the ecosystem-member management methods
 * (`fetchEcosystemMembers`, `updateEcosystemMemberRole`) used by the platform
 * admin area to view who belongs to a tenant ecosystem and to promote /
 * demote ecosystem admins. Same `isPlatformAdmin` gate server-side.
 */
export const useEcosystemApi = () => {
    const api = useAuthenticatedAxios();

    return useMemo(
        () => ({
            // Public directory of all active ecosystems.
            fetchEcosystems: async () => {
                const response = await api.get('/api/ecosystems');
                return response.data;
            },

            // The authenticated user's active ecosystem memberships, each
            // joined to its ecosystem (slug, name, shortName, branding) plus
            // the membership role and joinedAt.
            fetchMyMemberships: async () => {
                const response = await api.get('/api/users/me/memberships');
                return response.data;
            },

            // Join an ecosystem by slug (open-join, no approval gate).
            joinEcosystem: async (slug) => {
                const response = await api.post(`/api/ecosystems/${slug}/join`);
                return response.data;
            },

            // Leave an ecosystem by slug (soft-delete of the membership).
            leaveEcosystem: async (slug) => {
                const response = await api.delete(
                    `/api/ecosystems/${slug}/membership`
                );
                return response.data;
            },

            // --- Platform-admin CRUD (isPlatformAdmin-gated server-side) ---

            // List ALL tenant ecosystems regardless of status. Excludes the
            // platform pseudo-Ecosystem. Returns full documents + memberCount.
            fetchAllEcosystems: async () => {
                const response = await api.get('/api/ecosystems/admin/all');
                return response.data;
            },

            // Create a tenant ecosystem. `payload` carries slug, name,
            // shortName, description, branding, capabilities, taxonomy, and
            // status (defaults to 'pending' server-side when omitted).
            createEcosystem: async (payload) => {
                const response = await api.post('/api/ecosystems', payload);
                return response.data;
            },

            // Edit a tenant ecosystem by slug. The slug itself is immutable;
            // `payload` carries the editable fields plus an optional status.
            updateEcosystem: async (slug, payload) => {
                const response = await api.put(
                    `/api/ecosystems/${encodeURIComponent(slug)}`,
                    payload
                );
                return response.data;
            },

            // Soft-archive a tenant ecosystem by slug (status -> 'archived').
            archiveEcosystem: async (slug) => {
                const response = await api.post(
                    `/api/ecosystems/${encodeURIComponent(slug)}/archive`
                );
                return response.data;
            },

            // List a tenant ecosystem's memberships joined to User identity.
            // Returns rows of { userId, name, email, profilePicture, role,
            // status, joinedAt }.
            fetchEcosystemMembers: async (slug) => {
                const response = await api.get(
                    `/api/ecosystems/${encodeURIComponent(slug)}/members`
                );
                return response.data;
            },

            // Set a member's per-ecosystem role to 'admin' or 'member'.
            // Touches `role` only: membership `status` is unaffected.
            updateEcosystemMemberRole: async (slug, userId, role) => {
                const response = await api.put(
                    `/api/ecosystems/${encodeURIComponent(
                        slug
                    )}/members/${encodeURIComponent(userId)}/role`,
                    { role }
                );
                return response.data;
            },
        }),
        [api]
    );
};

export default useEcosystemApi;
